/* Spinach The Cow — magnify-on-hover for the product gallery.
 *
 * Drew asked for "thumbnails of the front and back and a hover zoom". The
 * thumbnails are a Horizon setting (templates/product.json). The hover zoom is
 * not: Horizon's own `zoom` setting opens a dialog on *click*, with drag and
 * pinch inside it. This adds the hover half and leaves that click alone.
 *
 * Deliberately plain script, not a Horizon `Component`. It owns no markup and
 * no state that outlives a pointer, so registering a custom element would buy
 * nothing and would tie a cosmetic effect to the theme's module graph.
 *
 * Delegated from the document, so it needs no re-binding when the carousel
 * swaps slides, when a variant swaps the media, or on a section re-render in
 * the theme editor — all three replace the elements this would otherwise be
 * bound to.
 */
(() => {
  const ZOOM = 2;
  const SELECTOR = '.product-information__media .product-media-container--zoomable';

  // No hover state on touch, and a transform cleared only by `mouseleave`
  // would stick after a tap. Checked once: a device does not grow a mouse.
  if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

  /** The zoomable container under the pointer, or null. */
  const containerFor = (target) => {
    if (!(target instanceof Element)) return null;
    const el = target.closest(SELECTOR);
    // The dialog renders its own copy of the gallery and has its own zoom.
    // Two zooms on one image is the failure this guards.
    if (!el || el.closest('.dialog-zoomed-gallery')) return null;
    return el;
  };

  const track = (el, event) => {
    const img = el.querySelector('.product-media__image');
    if (!img) return;
    const box = el.getBoundingClientRect();
    if (!box.width || !box.height) return;
    const x = ((event.clientX - box.left) / box.width) * 100;
    const y = ((event.clientY - box.top) / box.height) * 100;
    // Clamped so a pointer on the very edge cannot origin outside the image
    // and reveal a blank strip at the opposite edge.
    img.style.transformOrigin = `${Math.min(100, Math.max(0, x))}% ${Math.min(100, Math.max(0, y))}%`;
  };

  const stop = (el) => {
    if (!el) return;
    delete el.dataset.stcZooming;
    const img = el.querySelector('.product-media__image');
    if (img) img.style.transformOrigin = '';
  };

  let active = null;

  document.addEventListener(
    'pointermove',
    (event) => {
      // Only a mouse or a trackpad. A pen or a touch contact reports
      // `pointermove` too, and neither has a hover state to leave.
      if (event.pointerType !== 'mouse') return;
      const el = containerFor(event.target);
      if (el !== active) {
        stop(active);
        active = el;
        if (el) {
          el.style.setProperty('--stc-zoom', String(ZOOM));
          el.dataset.stcZooming = 'true';
        }
      }
      if (el) track(el, event);
    },
    { passive: true }
  );

  // `pointermove` alone never fires again once the pointer leaves the window,
  // so the last-hovered image would stay magnified behind whatever the visitor
  // moved on to.
  document.addEventListener('pointerleave', () => { stop(active); active = null; });
  window.addEventListener('blur', () => { stop(active); active = null; });
  // The click-to-open dialog covers the page; the image under it must not be
  // left mid-zoom for when it closes.
  document.addEventListener('click', () => { stop(active); active = null; });
})();
